import { CardShell } from '@atlas/ui-components'
import { Button, Chip, ListBoxItem, Select } from '@atlas/ui-primitives'
import { useState } from 'react'
import { PromptBodyDiff } from '@/components/prompts/PromptBodyDiff'
import type {
	Prompt,
	PromptLabelPointer,
	PromptModelConfig,
	PromptTestCaseSnapshot,
	PromptVersion,
} from '@/domain/prompt.types'
import { PromptLabel, PromptTestCaseOrigin } from '@/domain/prompt.types'
import { useMoveLabel, usePromptLabels, usePromptVersions } from '@/hooks/usePrompts'

/** Which labels point at a given version — the badges shown on its row. */
const labelsFor = (labels: PromptLabelPointer[], version: PromptVersion): PromptLabelPointer[] =>
	labels.filter((l) => l.promptVersionId === version.id)

export function PromptVersionsTab({ prompt }: { prompt: Prompt }) {
	const versions = usePromptVersions(prompt.id)
	const labels = usePromptLabels(prompt.id)
	const moveLabel = useMoveLabel()

	if (versions.isLoading) return <p className="text-ds-text-muted">Loading…</p>

	const rows = versions.data ?? []
	if (rows.length === 0) {
		return (
			<p className="rounded-ds-card border border-dashed border-ds-border p-8 text-center text-ds-text-muted">
				Never published. Publishing freezes the current body and variables as version 1.
			</p>
		)
	}

	const production = (labels.data ?? []).find((l) => l.label === PromptLabel.PRODUCTION)

	return (
		<div>
			{production?.pinned ? (
				<p className="mb-4 rounded-ds-md border border-ds-border bg-ds-surface p-3 text-ds-sm text-ds-text-muted">
					Production is <strong>pinned</strong>: publishing a new version will not move it. Promote explicitly to change
					what agents resolve.
				</p>
			) : (
				<p className="mb-4 rounded-ds-md border border-ds-border bg-ds-surface p-3 text-ds-sm text-ds-text-muted">
					Production follows the newest publish. Promoting a specific version pins it.
				</p>
			)}

			{moveLabel.isError ? (
				<p role="alert" className="mb-3 text-ds-sm text-ds-danger">
					The label could not be moved.
				</p>
			) : null}

			{/*
				Keyed on the newest version so publishing REMOUNTS the comparer.
				Its from/to live in `useState` initializers, which run once — so after a publish
				the list re-rendered with the new version while the comparer stayed pinned to the
				previous pair, showing a v1→v2 diff to someone who had just published v3 and come
				here to see what changed. The new version was in the dropdown, so the wrong diff
				looked authoritative rather than broken.
			*/}
			<VersionComparer key={rows[0]?.id} versions={rows} />

			<ul aria-label="Published versions" className="space-y-3">
				{rows.map((version) => (
					<VersionRow
						key={version.id}
						version={version}
						pointing={labelsFor(labels.data ?? [], version)}
						isPromoting={moveLabel.isPending}
						onPromote={() =>
							moveLabel.mutate({ id: prompt.id, label: PromptLabel.PRODUCTION, version: version.version })
						}
					/>
				))}
			</ul>
		</div>
	)
}

/** One published version: its labels, frozen config and last eval result. */
function VersionRow({
	version,
	pointing,
	isPromoting,
	onPromote,
}: {
	version: PromptVersion
	pointing: PromptLabelPointer[]
	isPromoting: boolean
	onPromote: () => void
}) {
	const isProduction = pointing.some((l) => l.label === PromptLabel.PRODUCTION)
	const summary = version.evalSummary
	// Typed once here rather than cast per field: the config is `unknown` on the DTO,
	// and three separate inline casts drifted from the interface as it gained fields.
	const modelConfig = version.modelConfig as PromptModelConfig | null
	const testCases = version.testCaseSnapshot

	return (
		<CardShell as="li" className="p-4">
			<div className="flex items-start justify-between gap-3">
				<div>
					<div className="flex items-center gap-2">
						<span className="font-mono text-ds-sm text-ds-text">v{version.version}</span>
						{pointing.map((l) => (
							<Chip key={l.label} variant={l.label === PromptLabel.PRODUCTION ? 'primary' : 'muted'} size="sm">
								{l.label}
								{l.pinned ? ' · pinned' : ''}
							</Chip>
						))}
					</div>
					<p className="mt-1 text-ds-2xs text-ds-text-subtle">
						Published {new Date(version.publishedAt).toLocaleString()}
						{version.publishedBy ? ` by ${version.publishedBy}` : ''}
					</p>
				</div>

				{/* Promotion is a label repoint — it creates no new version. */}
				{isProduction ? null : (
					<Button size="sm" variant="secondary" isDisabled={isPromoting} onPress={onPromote}>
						Promote to production
					</Button>
				)}
			</div>

			{/*
				Collapsed by default, and the only way to read ONE version's frozen text:
				the comparer below shows a diff between two, which says what changed but
				never what a version actually says — and with a single published version
				there is nothing to diff it against at all.
			*/}
			<details className="mt-3">
				<summary className="cursor-pointer text-ds-2xs uppercase tracking-wide text-ds-text-subtle">
					Frozen body and variables
				</summary>
				<pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-ds-md border border-ds-border bg-ds-surface p-3 font-mono text-ds-2xs text-ds-text">
					{version.body}
				</pre>
				{version.variables.length > 0 ? (
					<ul className="mt-2 flex flex-wrap gap-1.5">
						{version.variables.map((variable) => (
							<li key={variable.key}>
								<Chip variant="muted" size="sm">
									{variable.key}
									{variable.required ? ' *' : ''}
								</Chip>
							</li>
						))}
					</ul>
				) : (
					<p className="mt-2 text-ds-2xs text-ds-text-subtle">No variables — this version renders as fixed text.</p>
				)}
			</details>

			<FrozenTestCases cases={testCases} />

			<dl className="mt-3 grid gap-2 text-ds-sm sm:grid-cols-3">
				<div>
					<dt className="text-ds-2xs text-ds-text-subtle">Model</dt>
					<dd className="font-mono text-ds-text">{modelConfig?.model ?? '—'}</dd>
				</div>
				<div>
					<dt className="text-ds-2xs text-ds-text-subtle">Temperature</dt>
					<dd className="font-mono text-ds-text">{String(modelConfig?.temperature ?? '—')}</dd>
				</div>
				<div>
					<dt className="text-ds-2xs text-ds-text-subtle">Last eval</dt>
					<dd className="text-ds-text">
						{summary ? (
							// Errored counts against the run as much as failed: a case that never
							// executed proves nothing. Colouring on `failed` alone painted a run of
							// 8 errored and 0 failed green, which read as a pass.
							<span className={summary.failed + summary.errored > 0 ? 'text-ds-danger' : 'text-ds-success'}>
								{summary.passed} passed · {summary.failed} failed
								{summary.errored > 0 ? ` · ${summary.errored} errored` : ''}
							</span>
						) : (
							<span className="text-ds-text-muted">Never run</span>
						)}
					</dd>
				</div>
				{modelConfig?.negativePrompt ? (
					<div className="sm:col-span-3">
						<dt className="text-ds-2xs text-ds-text-subtle">Negative prompt</dt>
						<dd className="text-ds-text">{modelConfig.negativePrompt}</dd>
					</div>
				) : null}
			</dl>
		</CardShell>
	)
}

/**
 * Compare any two published versions' bodies.
 *
 * Defaults to the two newest, which is the comparison a reviewer almost always wants
 * ("what changed in the version I just published"), while still allowing any pair.
 * Hidden entirely with a single version, where there is nothing to compare.
 */
function VersionComparer({ versions }: { versions: PromptVersion[] }) {
	// Newest first from the API, so index 1 is the previous version and index 0 the newest.
	const [fromVersion, setFromVersion] = useState<number | null>(versions[1]?.version ?? null)
	const [toVersion, setToVersion] = useState<number | null>(versions[0]?.version ?? null)

	if (versions.length < 2) return null

	const from = versions.find((v) => v.version === fromVersion)
	const to = versions.find((v) => v.version === toVersion)

	return (
		<section className="mb-4 rounded-ds-card border border-ds-border p-4">
			<h3 className="mb-3 text-ds-2xs uppercase tracking-wide text-ds-text-subtle">Compare bodies</h3>

			<div className="mb-3 flex flex-wrap items-center gap-2">
				<span className="text-ds-sm text-ds-text-muted">From</span>
				<Select
					aria-label="Compare from version"
					selectedKey={fromVersion ?? undefined}
					onSelectionChange={(key) => setFromVersion(Number(key))}
				>
					{versions.map((v) => (
						<ListBoxItem key={v.id} id={v.version}>
							v{v.version}
						</ListBoxItem>
					))}
				</Select>
				<span className="text-ds-sm text-ds-text-muted">To</span>
				<Select
					aria-label="Compare to version"
					selectedKey={toVersion ?? undefined}
					onSelectionChange={(key) => setToVersion(Number(key))}
				>
					{versions.map((v) => (
						<ListBoxItem key={v.id} id={v.version}>
							v{v.version}
						</ListBoxItem>
					))}
				</Select>
			</div>

			{from && to ? (
				<PromptBodyDiff
					oldBody={from.body}
					newBody={to.body}
					oldLabel={`v${from.version}`}
					newLabel={`v${to.version}`}
				/>
			) : null}
		</section>
	)
}

/**
 * The test cases a version was evaluated against, frozen at publish.
 *
 * `undefined`/`null` means the version predates the snapshot, which is a different
 * statement from an empty array — that one says the version really was published
 * with nothing to evaluate it against, so it renders as such rather than as absent.
 */
function FrozenTestCases({ cases }: { cases?: PromptTestCaseSnapshot[] | null }) {
	if (cases === null || cases === undefined) return null

	return (
		<details className="mt-2">
			<summary className="cursor-pointer text-ds-2xs uppercase tracking-wide text-ds-text-subtle">
				Frozen test cases ({cases.length})
			</summary>
			{cases.length > 0 ? (
				<ul className="mt-2 space-y-1.5">
					{cases.map((testCase, index) => (
						<li key={`${testCase.name}-${index}`} className="rounded-ds-md border border-ds-border bg-ds-surface p-2">
							<div className="flex items-center gap-2">
								<span className="text-ds-2xs text-ds-text">{testCase.name}</span>
								{testCase.origin === PromptTestCaseOrigin.GENERATED ? (
									<Chip variant="muted" size="sm">
										AI drafted
									</Chip>
								) : null}
							</div>
							<p className="mt-1 font-mono text-ds-2xs text-ds-text-subtle">
								{Object.keys(testCase.values).length > 0 ? Object.keys(testCase.values).join(', ') : 'no values'}
								{' · '}
								{testCase.assertions.length} assertion{testCase.assertions.length === 1 ? '' : 's'}
							</p>
						</li>
					))}
				</ul>
			) : (
				<p className="mt-2 text-ds-2xs text-ds-text-subtle">
					Published with no active test cases — this version was never evaluated against anything.
				</p>
			)}
		</details>
	)
}
